"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { logger } from "@/utils/logger";
import { copyToClipboard, createShareableSearchUrl } from "@/utils/share-search";
import { Filters } from "@/types/filters";
import { ControlPosition, MapControl, useMap } from "@vis.gl/react-google-maps";
import { Check, Eye, Map as MapIcon, MapPin, Maximize, MoreHorizontal, Satellite, Share, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useState } from "react";

// Import the URL state hook
import { useContext } from "react";
import { UrlStateContext } from "@/hooks/use-url-state";

// Helper hook that safely gets filters without throwing
// when the UrlStateProvider is not available (e.g., on project pages with nearby places map)
function useSafeUrlFilters() {
  const $ = _c(3);
  const context = useContext(UrlStateContext);
  if (!context) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        filters: {},
        hasProvider: false
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== context.state.filters) {
    t0 = {
      filters: context.state.filters,
      hasProvider: true
    };
    $[1] = context.state.filters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
interface MapControlsFABProps {
  className?: string;
  buttonClassName?: string;
  fabClassName?: string;
}
export function MapControlsFAB({
  className,
  buttonClassName,
  fabClassName
}: MapControlsFABProps) {
  const map = useMap();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentMapType, setCurrentMapType] = useState<"roadmap" | "satellite" | "hybrid" | "terrain">("roadmap");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const city = useCurrentCityName();

  // Try to get filters but don't fail if provider is not available
  const {
    filters,
    hasProvider
  } = useSafeUrlFilters();

  // Default button classes
  const defaultButtonClasses = "bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 w-8 h-8";
  const defaultFabClasses = "bg-black text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 w-8 h-8 data-[hovered]:bg-black data-[hovered]:text-white";

  // Merge with custom classes
  const buttonClasses = buttonClassName || defaultButtonClasses;
  const fabClasses = fabClassName || defaultFabClasses;

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);
  const toggleMapType = () => {
    if (!map) return;
    const types: ("roadmap" | "satellite" | "hybrid" | "terrain")[] = ["roadmap", "satellite", "hybrid", "terrain"];
    const currentIndex = types.indexOf(currentMapType);
    const nextIndex = (currentIndex + 1) % types.length;
    const newType = types[nextIndex];
    map.setMapTypeId(newType);
    setCurrentMapType(newType);
  };
  const toggleFullscreen = () => {
    if (!map) return;
    const mapContainer = map.getDiv();
    if (mapContainer) {
      const fullscreenElement = document.fullscreenElement ||
      // @ts-ignore
      document.webkitFullscreenElement ||
      // @ts-ignore
      document.mozFullScreenElement ||
      // @ts-ignore
      document.msFullscreenElement;
      if (fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if ((document as any).webkitExitFullscreen) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (document as any).webkitExitFullscreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if ((document as any).mozCancelFullScreen) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (document as any).mozCancelFullScreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if ((document as any).msExitFullscreen) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (document as any).msExitFullscreen();
        }
      } else {
        if (typeof mapContainer.requestFullscreen === "function") {
          mapContainer.requestFullscreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (typeof (mapContainer as any).webkitRequestFullscreen === "function") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (mapContainer as any).webkitRequestFullscreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (typeof (mapContainer as any).mozRequestFullScreen === "function") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (mapContainer as any).mozRequestFullScreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (typeof (mapContainer as any).msRequestFullscreen === "function") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (mapContainer as any).msRequestFullscreen();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (typeof (mapContainer as any).webkitEnterFullscreen === "function") {
          // iOS Safari specific for video, but might as well check for others?
          // Actually iOS Safari doesn't support generic element fullscreen (only video).
          // So this block handles the "function not found" case gracefully by doing nothing if not supported.
        }
      }
    }
  };
  const zoomIn = () => {
    if (!map) return;
    const currentZoom = map.getZoom();
    if (currentZoom !== undefined) {
      map.setZoom(currentZoom + 1);
    }
  };
  const zoomOut = () => {
    if (!map) return;
    const currentZoom_0 = map.getZoom();
    if (currentZoom_0 !== undefined) {
      map.setZoom(currentZoom_0 - 1);
    }
  };
  const centerOnLocation = () => {
    if (!map) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const {
          latitude,
          longitude
        } = position.coords;
        map.setCenter({
          lat: latitude,
          lng: longitude
        });
        map.setZoom(15);
      }, error => {
        // Silent failure - user can still manually navigate the map
        logger.debug("Error getting location for map centering:", error);
      });
    }
  };

  // Share functionality
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const {
        url
      } = createShareableSearchUrl(filters, city);

      // Try native share API first (mobile devices)
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        try {
          await navigator.share({
            title: `Property Search ${city ? `in ${city}` : ""}`,
            text: "Check out these properties I found",
            url: url
          });
          return;
        } catch (shareErr) {
          logger.debug("Failed to share:", shareErr);
        }
      }

      // Fallback to clipboard copy
      const success = await copyToClipboard(url);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error_0) {
      logger.error("Failed to share:", error_0);
    } finally {
      setIsSharing(false);
    }
  };

  // Use Google Maps' own control system for proper layering
  return <MapControl position={ControlPosition.RIGHT_BOTTOM}>
			<div className={`flex flex-col-reverse gap-2 items-end pr-4 pb-1 ${className}`}>
				{/* Main FAB */}
				<TooltipTrigger>
					<Button variant="ghost" onPress={() => {
          setIsExpanded(!isExpanded);
        }} className={`${fabClasses} rotate-0`} size="icon" aria-label={isExpanded ? "Hide map controls" : "Show map controls"} aria-expanded={isExpanded} aria-controls="map-controls-menu">
						{isExpanded ? <span className="text-xl leading-none">×</span> : <MoreHorizontal className="w-6 h-6" />}
					</Button>
					<Tooltip placement="left">{isExpanded ? "Hide map controls" : "Show map controls"}</Tooltip>
				</TooltipTrigger>

				{/* Expanded controls */}
				{isExpanded && <div id="map-controls-menu" className="flex flex-col gap-2 items-end animate-in fade-in slide-in-from-bottom-2 zoom-in-95 duration-300">
						{/* Map Type Toggle */}
						<TooltipTrigger>
							<Button onPress={toggleMapType} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label={`Switch to ${currentMapType === "roadmap" ? "satellite" : currentMapType === "satellite" ? "hybrid" : currentMapType === "hybrid" ? "terrain" : "roadmap"} view`}>
								{currentMapType === "roadmap" ? <Satellite className="w-5 h-5" /> : currentMapType === "satellite" ? <MapIcon className="w-5 h-5" /> : currentMapType === "hybrid" ? <Eye className="w-5 h-5" /> : <MapIcon className="w-5 h-5" />}
							</Button>
							<Tooltip placement="left">
								{`Switch to ${currentMapType === "roadmap" ? "satellite" : currentMapType === "satellite" ? "hybrid" : currentMapType === "hybrid" ? "terrain" : "roadmap"} view`}
							</Tooltip>
						</TooltipTrigger>

						{/* Fullscreen Toggle */}
						<TooltipTrigger>
							<Button onPress={toggleFullscreen} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
								<Maximize className="w-5 h-5" />
							</Button>
							<Tooltip placement="left">{isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}</Tooltip>
						</TooltipTrigger>

						{/* My Location */}
						<TooltipTrigger>
							<Button onPress={centerOnLocation} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Center on my location">
								<MapPin className="w-5 h-5" />
							</Button>
							<Tooltip placement="left">Center on my location</Tooltip>
						</TooltipTrigger>

						{/* Zoom In */}
						<TooltipTrigger>
							<Button onPress={zoomIn} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Zoom in">
								<ZoomIn className="w-5 h-5" />
							</Button>
							<Tooltip placement="left">Zoom in</Tooltip>
						</TooltipTrigger>

						{/* Zoom Out */}
						<TooltipTrigger>
							<Button onPress={zoomOut} isDisabled={!map} variant="ghost" size="icon" className={buttonClasses} aria-label="Zoom out">
								<ZoomOut className="w-5 h-5" />
							</Button>
							<Tooltip placement="left">Zoom out</Tooltip>
						</TooltipTrigger>

						{/* Share - only show if UrlStateProvider is available */}
						{hasProvider && <TooltipTrigger>
								<Button onPress={handleShare} isDisabled={isSharing} variant="ghost" size="icon" className={buttonClasses} aria-label="Share search">
									{copied ? <Check className="w-5 h-5 text-green-600" /> : <Share className="w-5 h-5" />}
								</Button>
								<Tooltip placement="left">{copied ? "Copied!" : "Share search"}</Tooltip>
							</TooltipTrigger>}
					</div>}
			</div>
		</MapControl>;
}